import type { ObjectId } from '@freightview/object-id'

import { fvDateUtc } from '@fv/dates'

import type { EquipmentType } from '../equipment/constants.ts'
import {
  vBoolean,
  vDateUtcNoon,
  vFallback,
  vNumber,
  vObject,
  vOptional,
  vOptionalDef,
  type vOutput,
  vPicklist,
  vString,
  vStringToNumber,
} from '../validation/index.ts'
import type { LoadBidExpiration } from './bid-expiration.ts'
import type { Currency, DistanceUOM, Workflow } from './common.ts'
import type { LoadCustomsBroker } from './customs-broker.ts'
import type { LoadExchangeRates } from './exchange-rate.ts'
import type { FuelPriceProviderCode } from './fuel.ts'
import type { LoadBillTo } from './load-bill-to.ts'
import type { LoadBOLDetail } from './load-bol.ts'
import type { LoadDocumentDTO } from './load-document.ts'
import type { LoadEmergencyContact } from './load-emergency-contact.ts'
import type { LoadEquipment } from './load-equipment.ts'
import type { LoadItem } from './load-item.ts'
import type { LoadLocation } from './load-location.ts'
import type { LoadPickupDetail } from './load-pickup.ts'
import type { SaltDetails } from './load-salt.ts'
import { loadLocationAccessorialSchema } from './location-accessorial.ts'
import { createPaginationSchema } from './paged.ts'
import type { LoadRefNum } from './ref-num.ts'
import type { RouteGuideExecutionStep } from './route-guide.ts'
import { type SortDir, sortDirSchema } from './sort.ts'
import { type TrackDetail, trackStatuses } from './track.ts'

export const opportunitySorts = [
  'pickupDate',
  'bidExpirationDate',
  'lastMessageDate',
] as const
export type OpportunitySort = (typeof opportunitySorts)[number]

export interface OpportunitySortBase {
  sortBy?: OpportunitySort
  sortDirection?: SortDir
}

export const opportunityStatuses = [
  'open',
  'offered',
  'declined',
  'lost',
  'awarded',
  'confirmed',
] as const
export type OpportunityStatus = (typeof opportunityStatuses)[number]

const MAX_LIMIT = 20

export const appointmentStatuses = ['appointments']
export type AppointmentStatus = (typeof appointmentStatuses)[number]

export const opportunityFilterSchema = vObject({
  ...createPaginationSchema({ limit: MAX_LIMIT }).entries,
  accountId: vOptional(vString()),
  status: vPicklist(opportunityStatuses),
  isArchived: vOptional(vBoolean()),
  trackingStatus: vOptional(vPicklist(trackStatuses)),
  appointmentStatus: vOptional(vPicklist(appointmentStatuses)),
  sortBy: vFallback(vPicklist(opportunitySorts), 'pickupDate'),
  sortDirection: vFallback(sortDirSchema, 1),
})
export type OpportunityFilterDTO = vOutput<typeof opportunityFilterSchema>

export const opportunityBucketCountQuerySchema = vObject({
  accountId: vOptional(vString()),
})
export type OpportunityBucketCountDto = vOutput<
  typeof opportunityBucketCountQuerySchema
>

export type OpportunityBucketCountResponse = {
  awarded: number
  confirmed: number
  offered: number
  open: number
  pendingTenders: number
}

export const opportunityDetailsSchema = vObject({
  carrierName: vOptional(vString()),
  trackingNumber: vOptional(vString()),
  trailerNumber: vOptional(vString()),
  sealNumber: vOptional(vString()),
})
export type OpportunityDetailsDto = vOutput<typeof opportunityDetailsSchema>

export const carrierLoadSearchSchema = vObject({
  limit: vOptionalDef(vStringToNumber(), MAX_LIMIT),
  skip: vOptionalDef(vStringToNumber(), 0),
  startDate: vFallback(
    vDateUtcNoon(),
    fvDateUtc().subtract(1, 'years').toDate(),
  ),
  searchText: vString(),
})
export type CarrierLoadSearchDTO = vOutput<typeof carrierLoadSearchSchema>

export const archiveCanceledSchema = vObject({
  carrierId: vOptional(vString()),
})
export type ArchiveCanceledDTO = vOutput<typeof archiveCanceledSchema>

export interface AccessControlDetail {
  userId: string
}
interface HowToBook {
  schedulePickup: boolean
  createBOL: boolean
}
interface ShipperDetails {
  contactEmail: string
  contactName?: string
  userId: string
  accountId: string
  companyName: string
}
export interface Opportunity {
  _id?: ObjectId
  accessControl: AccessControlDetail[]
  bidExpiration?: LoadBidExpiration
  billTo?: LoadBillTo
  bol?: LoadBOLDetail
  carrierId?: string
  carrierName?: string
  cheapestQuoteAmount?: number
  cheapestQuoteCurrency: Currency
  createdDate: Date
  customsBroker?: LoadCustomsBroker[]
  distance?: number
  distanceUOM?: DistanceUOM
  documents?: LoadDocumentDTO[]
  emergencyContact?: LoadEmergencyContact
  equipmentRequested: LoadEquipment
  exchangeRates?: LoadExchangeRates
  fuel?: {
    cost: number
    provider: FuelPriceProviderCode
  }
  howToBook?: HowToBook
  isArchived?: boolean
  isCanceled?: boolean
  isFreightCollect?: boolean
  isLiveLoad: boolean
  isLowest: boolean
  isMultiTruck?: boolean
  items: LoadItem[]
  loadId: string
  locations: LoadLocation[]
  lowestBid?: number
  lowestBidCurrency?: Currency
  pickup?: LoadPickupDetail
  pickupDate: Date
  quotedEquipmentType?: EquipmentType
  quoteRequestId: string
  refNums: LoadRefNum[]
  requestDate: Date
  searchFields: string[]
  shipper: ShipperDetails
  showPricingIndicator?: boolean
  status: 'open' | 'offered' | 'declined' | 'lost' | 'awarded' | 'confirmed'
  targetPrice?: number
  saltDetails?: SaltDetails
  track?: TrackDetail
  allowAppointmentUpdates: boolean
  /** Only what the carrier needs: tender order and rates stay internal to the shipper. */
  waterfallTender?: Pick<RouteGuideExecutionStep, 'expiresAt'>
  workflow: Workflow
}

export const accessorialUpdateSchema = vObject({
  accessorial: loadLocationAccessorialSchema,
  sequence: vNumber(),
})

export type AccessorialUpdateDTO = vOutput<typeof accessorialUpdateSchema>
